import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Expense } from '../data/expenses';
import { colorFor } from '../data/palette';

interface ChecklistItemProps {
  item: Expense;
  index: number;
  onToggle: (id: string) => void;
  onEdit: (id: string) => void;
}

function formatDate(date: string): string {
  if (!date) return '';
  const d = new Date(`${date}T00:00:00`);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString('en-HK', { day: 'numeric', month: 'short' });
}

/** Whole days since the due date (local time), minimum 0. Only used for overdue notes. */
function daysOverdue(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return 0;
  const dueUtc = Date.UTC(y, m - 1, d);
  const now = new Date();
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.floor((todayUtc - dueUtc) / 86400000));
}

/** Stable per-id tilt (-2..2deg) so notes don't "jump" when reordered */
function tiltFor(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return (hash % 5) - 2;
}

const ChecklistItem: React.FC<ChecklistItemProps> = ({ item, index, onToggle, onEdit }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const color = colorFor(item);
  const dateLabel = formatDate(item.date);
  const isOverdue =
    !!item.date && !item.completed && item.date < new Date().toISOString().slice(0, 10);
  const overdueDays = isOverdue ? daysOverdue(item.date) : 0;

  /** App links: either `linkType: 'app'` or a non-http(s) value (custom scheme). */
  const isAppLink = item.linkType === 'app' || !/^https?:\/\//i.test(item.link);

  const openLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.link) return;
    if (/^https?:\/\//i.test(item.link)) {
      // Website URL: open in Safari (external links always leave a standalone PWA).
      window.open(item.link, '_blank', 'noopener,noreferrer');
    } else {
      // Custom scheme (e.g. hsbc-hk://): WebKit intercepts the navigation and
      // shows the OS "Open in …?" prompt. window.open is unreliable for schemes,
      // so click a temporary anchor instead (user-gesture-safe).
      const a = document.createElement('a');
      a.href = item.link;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <div
      ref={setNodeRef}
      className={`sortable ${isDragging ? 'sortable--dragging' : ''}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <div
        className={`note ${item.completed ? 'note--done' : ''} ${isOverdue ? 'note--overdue' : ''}`}
        style={
          {
            '--bg': color.bg,
            '--edge': color.edge,
            '--ink': color.ink,
            '--rot': `${tiltFor(item.id)}deg`,
            '--i': index,
          } as React.CSSProperties
        }
        onClick={() => onEdit(item.id)}
        role="button"
        tabIndex={0}
        aria-label={`Edit ${item.title || 'note'}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onEdit(item.id);
          }
        }}
      >
        <span className="note-tape" aria-hidden="true" />
        <button
          type="button"
          className={`tick ${item.completed ? 'tick--on' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(item.id);
          }}
          aria-label={item.completed ? `Mark ${item.title || 'note'} as unpaid` : `Mark ${item.title || 'note'} as paid`}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
            <path
              className="tick-path"
              d="M5 12.5l4.5 4.5L19 7.5"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="note-body">
          {dateLabel && (
            <div
              className={`note-date ${isOverdue ? 'note-date--overdue' : ''} ${item.completed ? 'note-date--paid' : ''}`}
              title={item.date || undefined}
            >
              <span className="note-date-text">
                {item.completed ? '✓ Paid' : isOverdue ? `Overdue · ${overdueDays}d` : dateLabel}
              </span>
            </div>
          )}

          <h3 className="note-title">
            {isOverdue && (
              <span className="note-warn" role="img" aria-label="Overdue">
                ⚠️
              </span>
            )}
            {item.title || 'Untitled bill'}
          </h3>

          {item.description && <p className="note-desc">{item.description}</p>}

          <div className="note-actions">
            {item.link && (
              <button
                type="button"
                className="note-goto"
                onClick={openLink}
                aria-label={`Open ${item.title || 'note'} ${isAppLink ? 'app' : 'link'}`}
                title={item.link}
              >
                {isAppLink ? (
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
                    <path
                      d="M7 2.5h10A1.5 1.5 0 0 1 18.5 4v16a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 20V4A1.5 1.5 0 0 1 7 2.5z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M10.5 18.5h3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
                    <path
                      d="M7 17L17 7M9 7h8v8"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            )}
            <button
              type="button"
              className="drag-handle"
              {...attributes}
              {...listeners}
              aria-label={`Drag to reorder ${item.title || 'note'}`}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                <circle cx="9" cy="6" r="1.6" />
                <circle cx="15" cy="6" r="1.6" />
                <circle cx="9" cy="12" r="1.6" />
                <circle cx="15" cy="12" r="1.6" />
                <circle cx="9" cy="18" r="1.6" />
                <circle cx="15" cy="18" r="1.6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChecklistItem;
